/**
 * SketchPlane - 2D Drawing Canvas Manager
 * Simplified version for Phase 2 demo
 *
 * Manages a collection of SketchPaths on a 2D drawing plane.
 */

import { SketchPath } from './SketchPath.js';
import { SketchPoint } from './SketchPoint.js';
import { SketchSpline } from './SketchSpline.js';
import { SketchShape } from './SketchShape.js';
import { Vec2D } from './geometry/Vec2D.js';

export class SketchPlane {
    /**
     * Is there a draggable sketch point (spline centre point, bezier handle,
     * or path point) within radius of (x, y)? Used so control-point grabs
     * take priority over rib-chord drags.
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     * @param {number} radius - Hit radius in sketch px
     * @returns {boolean}
     */
    findSplineNearCentreLine(x, y, radius) {
        const segDist = (px, py, ax, ay, bx, by) => {
            const dx = bx - ax, dy = by - ay;
            const len2 = dx * dx + dy * dy;
            const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
            return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
        };
        for (const spline of this.splines) {
            const pts = spline.centrePath?.points || [];
            for (let i = 0; i + 1 < pts.length; i++) {
                const a = pts[i], b = pts[i + 1];
                // Sample beziers so curved lines are grabbable too
                if (a.controlPoint2 || b.controlPoint1) {
                    const c1 = a.controlPoint2 || a, c2 = b.controlPoint1 || b;
                    let prev = a;
                    for (let k = 1; k <= 8; k++) {
                        const t = k / 8, t1 = 1 - t;
                        const cur = {
                            x: t1*t1*t1*a.x + 3*t1*t1*t*c1.x + 3*t1*t*t*c2.x + t*t*t*b.x,
                            y: t1*t1*t1*a.y + 3*t1*t1*t*c1.y + 3*t1*t*t*c2.y + t*t*t*b.y
                        };
                        if (segDist(x, y, prev.x, prev.y, cur.x, cur.y) <= radius) return spline;
                        prev = cur;
                    }
                } else if (segDist(x, y, a.x, a.y, b.x, b.y) <= radius) {
                    return spline;
                }
            }
        }
        return null;
    }

    hasGrabbablePointAt(x, y, radius) {
        // The centre LINE is grabbable too (whole-spline drag) - but only
        // on SELECTED shapes (unselected shapes show no overlay)
        const lineSpline = this.findSplineNearCentreLine(x, y, radius);
        if (lineSpline && this.selectedShapes?.has(lineSpline)) return true;

        const near = (p) => p && Math.hypot(p.x - x, p.y - y) <= radius;

        // Anchor points are grabbable on ANY shape (a click on an
        // unselected shape's point selects-and-grabs in one gesture);
        // bezier handles only on selected shapes (invisible otherwise)
        for (const spline of this.splines || []) {
            const shapeSelected = this.selectedShapes?.has(spline);
            for (const pt of spline.centrePath?.points || []) {
                if (near(pt)) return true;
                if (shapeSelected && (near(pt.controlPoint1) || near(pt.controlPoint2))) return true;
            }
        }
        for (const path of this.paths || []) {
            const shapeSelected = this.selectedShapes?.has(path);
            for (const pt of path.points || []) {
                if (near(pt)) return true;
                if (shapeSelected && (near(pt.controlPoint1) || near(pt.controlPoint2))) return true;
            }
        }
        return false;
    }

    /**
     * NON-MUTATING hit test for the edit tools (select/bezier): would a
     * pointer-down at (x, y) hit anything - handle, anchor point, centre
     * line or shape body? Mirrors the exact priority checks of
     * handlePointerDown3D without selecting/grabbing/creating anything.
     *
     * Used by Application's pointer-down to resolve PARALLAX under scene
     * rotation: each selected base plane's ray intersection yields
     * DIFFERENT sketch coordinates for the same click, so the caller
     * probes every plane's coordinates with this test and dispatches the
     * real pointer-down with the first plane that hits. (Running the full
     * pointer-down once per plane and checking afterwards mutated the
     * selection per attempt - only the last plane could ever stay
     * selected, so with the model rotated, select/move appeared to work
     * on a single layer only.)
     *
     * Returns a PRIORITY CLASS rather than a boolean so the caller can
     * rank hits ACROSS planes by type first, plane order second: a
     * precise control-point hit on a far plane must beat a big shape-BODY
     * hit on a near plane, otherwise overlapping layers under rotation
     * make points ungrabbable.
     * @returns {number} 0 = miss, else 1 handle / 2 anchor point /
     *          3 centre line / 4 shape body (lower = higher priority)
     */
    hitTestEditTool(x, y, hitRadius) {
        if (this.tool === 'select') {
            // 1: bezier handles (only visible/grabbable on SELECTED shapes)
            for (const spline of this.splines) {
                if (!this.selectedShapes.has(spline)) continue;
                if (spline.findBezierHandleNear(x, y, hitRadius)) return 1;
            }
            for (const path of this.paths) {
                if (!this.selectedShapes.has(path)) continue;
                if (this.findHandleInList(path.points, x, y, hitRadius)) return 1;
            }
            // 2: anchor points on ANY shape (select-and-grab)
            for (const spline of this.splines) {
                if (spline.findPointNear(x, y, hitRadius)) return 2;
            }
            for (const path of this.paths) {
                if (this.findPointInList(path.points, x, y, hitRadius)) return 2;
            }
            // 3: the centre line (same enlarged radius as the grab)
            if (this.findSplineNearCentreLine(x, y, hitRadius * 1.2)) return 3;
            // 4: shape body
            if (this.findShapeAt(x, y)) return 4;
            return 0;
        }

        if (this.tool === 'bezier') {
            // Existing handles or anchor points on any shape
            const targets = [
                ...this.splines.map(s => s.centrePath.points),
                ...this.paths.map(p => p.points)
            ];
            for (const points of targets) {
                if (this.findHandleInList(points, x, y, hitRadius)) return 1;
            }
            for (const points of targets) {
                if (this.findPointInList(points, x, y, hitRadius)) return 2;
            }
            return 0;
        }

        return 0;
    }

    /**
     * Is this shape (spline or pen path) in the current shape selection?
     */
    isShapeSelected(shape) {
        return this.selectedShapes.has(shape);
    }

    /**
     * Select a shape (spline or pen path).
     * @param {Object} shape - SketchSpline or SketchPath
     * @param {Object} [opts]
     * @param {boolean} [opts.toggle=false]  - SHIFT semantics: add if absent,
     *        REMOVE if already selected
     * @param {boolean} [opts.additive=false] - add without clearing others
     * @returns {boolean} true if the shape ends up selected
     */
    selectShape(shape, { toggle = false, additive = false } = {}) {
        if (!shape) return false;

        if (toggle && this.selectedShapes.has(shape)) {
            this.selectedShapes.delete(shape);
            shape.clearSelection?.();
            if (this.selectedSpline === shape) this.selectedSpline = null;
            if (this.selectedPath === shape) this.selectedPath = null;
            return false;
        }

        if (!toggle && !additive) {
            this.clearShapeSelection();
        }

        this.selectedShapes.add(shape);
        if (this.splines.includes(shape)) {
            this.selectedSpline = shape;
            this.selectedPath = null;
        } else {
            this.selectedPath = shape;
            this.selectedSpline = null;
        }
        return true;
    }

    /**
     * Clear the shape selection (and all per-point node selections)
     */
    clearShapeSelection() {
        this.selectedShapes.clear();
        this.splines.forEach(s => s.clearSelection?.());
        this.selectedSpline = null;
        this.selectedPath = null;
    }

    /**
     * Find the topmost shape whose BODY (outline polygon) contains (x, y).
     * Splines are tested against their built outline; pen paths against
     * their polygon. Latest-drawn shape wins.
     * @returns {Object|null} SketchSpline or SketchPath
     */
    findShapeAt(x, y) {
        const inRing = (pts) => {
            let inside = false;
            for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
                if ((pts[i].y > y) !== (pts[j].y > y) &&
                    x < (pts[j].x - pts[i].x) * (y - pts[i].y) / (pts[j].y - pts[i].y) + pts[i].x) {
                    inside = !inside;
                }
            }
            return inside;
        };

        for (let i = this.splines.length - 1; i >= 0; i--) {
            const pts = this.splines[i].getPath?.()?.points;
            if (pts && pts.length >= 3 && inRing(pts)) return this.splines[i];
        }
        for (let i = this.paths.length - 1; i >= 0; i--) {
            const pts = this.paths[i].points;
            if (pts && pts.length >= 3 && inRing(pts)) return this.paths[i];
        }
        return null;
    }

    /**
     * Create a new sketch plane
     * @param {HTMLCanvasElement} canvas - Canvas element (may be null in 3D mode)
     * @param {Object} options - Configuration options
     * @param {string} options.mode - '2d' (legacy canvas) or '3d' (unified view)
     */
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this.ctx = canvas ? canvas.getContext('2d') : null;

        // Phase 3.5: Drawing mode
        this.mode = options.mode || '2d'; // '2d' = legacy canvas, '3d' = unified 3D view

        // Shapes collection
        this.paths = [];
        this.splines = []; // Brush strokes (SketchSpline objects)
        this.selectedPath = null;
        this.closedShapes = []; // Closed paths ready for 3D generation (applied to all layers)
        this.legShapes = []; // Leg shapes (applied only to outer layers)

        // SHAPE-level selection (Figma-like): overlays and point/handle
        // grabbing only apply to shapes in this set. selectedSpline /
        // selectedPath remain the PRIMARY selected shape (widgets, delete).
        this.selectedShapes = new Set();

        // Current drawing state
        this.currentPath = null;
        this.currentSpline = null; // Current brush stroke being drawn
        this.isDrawing = false;
        this.lastPoint = null; // Track last point for distance checking

        // Tool state
        this.tool = 'brush'; // 'pen', 'brush', 'select', 'move'
        this.autoClose = true; // Auto-close paths on finish
        this.brushWidth = 40; // Brush width in pixels (matches Java default)
        this.currentCapType = 1; // Default cap type (CAP_ROUND)

        // Select tool state
        this.selectedSpline = null;
        this.draggedPoint = null;

        // View transform
        this.viewOffset = new Vec2D(0, 0);
        this.viewScale = 1.0;

        // Settings
        this.gridSize = 20;
        this.showGrid = true;
        this.snapToGrid = false;

        // Globals (for compatibility with Java version)
        this.sketchGlobals = {
            sketch_id: 0,
            BEZIER_DETAIL_CALCULATIONS: 0.05
        };

        // Callbacks
        this.onShapeChangeCallback = null;
        this.onDragPreviewCallback = null;
        this.onBeforeCommitCallback = null; // Fired BEFORE a mutation commits (undo snapshots)

        // Setup interaction (only in 2D mode)
        if (this.mode === '2d' && this.canvas) {
            this.setupInteraction();
        }
    }

    /**
     * Set callback for when shapes change
     * @param {Function} callback - Callback function
     */
    setOnShapeChange(callback) {
        this.onShapeChangeCallback = callback;
    }

    /**
     * Trigger shape change callback
     */
    onShapeChange() {
        if (this.onShapeChangeCallback) {
            this.onShapeChangeCallback(this.closedShapes, this.splines, this.legShapes);
        }
    }

    /**
     * Set callback fired just BEFORE a committed mutation (stroke finish,
     * point-drag start, deletion) - used by the undo system to snapshot
     * the pre-change state.
     */
    setOnBeforeCommit(callback) {
        this.onBeforeCommitCallback = callback;
    }

    /** Notify the undo system that a committed mutation is about to happen */
    onBeforeCommit() {
        if (this.onBeforeCommitCallback) {
            this.onBeforeCommitCallback();
        }
    }

    /**
     * Snapshot the COMMITTED sketch state (deep clones, excludes any
     * in-progress drawing) for undo/redo
     */
    captureState() {
        return {
            paths: this.paths.map(p => p.clone()),
            splines: this.splines.map(s => s.clone()),
            closedShapes: this.closedShapes.map(s => s.clone()),
            legShapes: this.legShapes.map(s => s.clone())
        };
    }

    /**
     * Restore a captured state (undo/redo).
     * closedShapes/legShapes are DERIVED from the restored paths and
     * splines rather than restored as separate clones - separate clones
     * would break the identity links rebuildShapeLists relies on.
     */
    restoreState(state) {
        this.cancelCurrentDrawing();
        this.paths = state.paths.map(p => p.clone());
        this.splines = state.splines.map(s => s.clone());
        this.splines.forEach(s => s.build());
        this.closedShapes = this.paths.filter(p => p.getClosed && p.getClosed());
        this.legShapes = [];
        this.selectedShapes.clear(); // shape objects were replaced by clones
        this.selectedSpline = null;
        this.selectedPath = null;
        this.draggedPoint = null;
        this.draggedHandleInfo = null;
        this.rebuildShapeLists();
        this.onShapeChange();
        if (this.mode === '2d') this.render();
    }

    /**
     * Set callback for lightweight preview updates during drag-editing.
     * Unlike onShapeChange, this must NOT trigger full mesh/slice regeneration -
     * it fires on every pointermove while dragging a control point.
     * @param {Function} callback - Callback function
     */
    setOnDragPreview(callback) {
        this.onDragPreviewCallback = callback;
    }

    /**
     * Trigger drag preview callback (falls back to onShapeChange if unset)
     */
    onDragPreview() {
        if (this.onDragPreviewCallback) {
            this.onDragPreviewCallback(this.selectedSpline || this.selectedPath);
        } else {
            this.onShapeChange();
        }
    }

    /**
     * Setup mouse/touch interaction
     */
    setupInteraction() {
        let lastPoint = null;

        // Pointer down - start drawing
        this.canvas.addEventListener('pointerdown', (e) => {
            // Keep the gesture's pointer stream on the canvas so releasing
            // over other UI (or outside the window) still delivers pointerup
            // and the in-progress stroke/leg commits instead of vanishing
            try { this.canvas.setPointerCapture(e.pointerId); } catch (_) { /* synthetic pointers */ }

            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const worldPos = this.screenToWorld(x, y);
            const isRightClick = e.button === 2;

            // PATH TOOL - Click to add points
            if (this.tool === 'pen') {
                console.log('[PATH TOOL] Click detected, isRightClick:', isRightClick, 'currentPath:', this.currentPath);
                if (isRightClick && this.currentPath) {
                    // Right click - finish path
                    console.log('[PATH TOOL] Finishing path, size:', this.currentPath.size());
                    if (this.currentPath.size() > 2) {
                        if (this.autoClose) {
                            this.currentPath.setClosed(true);
                            this.closedShapes.push(this.currentPath);
                        }
                        this.paths.push(this.currentPath);
                        this.onShapeChange();
                        console.log('[PATH TOOL] Path finished and added to shapes');
                    }
                    this.currentPath = null;
                    this.render();
                } else if (!isRightClick) {
                    // Left click - add point
                    if (!this.currentPath) {
                        this.currentPath = new SketchPath(this);
                        console.log('[PATH TOOL] Created new path');
                    }
                    this.currentPath.add(new SketchPoint(worldPos.x, worldPos.y));
                    console.log('[PATH TOOL] Added point, total points:', this.currentPath.size());
                    this.render();
                }
            }
            // OFFSET PATH TOOL - Click to add points
            else if (this.tool === 'offset') {
                console.log('[OFFSET TOOL] Click detected, isRightClick:', isRightClick, 'currentSpline:', this.currentSpline);
                if (isRightClick && this.currentSpline) {
                    // Right click - finish and build offset
                    console.log('[OFFSET TOOL] Finishing spline, size:', this.currentSpline.size());
                    // Remove the temporary mouse-following point FIRST, then
                    // require 2 real centre points - a 2-point offset path is
                    // a valid capsule outline (the old order counted the
                    // preview point and the guard rejected short paths)
                    if (this.currentSpline.centrePath.size() > 1) {
                        this.currentSpline.centrePath.points.pop();
                    }
                    if (this.currentSpline.size() >= 2) {
                        this.currentSpline.setClosed(true);
                        this.currentSpline.build();
                        this.splines.push(this.currentSpline);
                        this.closedShapes.push(this.currentSpline.getPath());
                        this.onShapeChange();
                        console.log('[OFFSET TOOL] Spline finished and added to shapes');
                    }
                    this.currentSpline = null;
                    this.render();
                } else if (!isRightClick) {
                    // Left click - add point
                    if (!this.currentSpline || this.currentSpline.centrePath.getClosed()) {
                        // Create new offset spline
                        this.currentSpline = new SketchSpline(this);
                        this.currentSpline.setType(SketchShape.OFFSET_SPLINE);
                        this.currentSpline.setOffsetSize(this.brushWidth);
                        this.currentSpline.capType = this.currentCapType;
                        this.currentSpline.joinType = SketchSpline.JOIN_ROUND;
                        this.currentSpline.autoSmooth = false;
                        console.log('[OFFSET TOOL] Created new spline');
                    }

                    // Add the clicked point
                    this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                    // Add a temporary second point (will be replaced on next click or removed on finish)
                    this.currentSpline.add(new SketchPoint(worldPos.x + 10, worldPos.y + 1));

                    console.log('[OFFSET TOOL] Added point, total points:', this.currentSpline.size());

                    // Build the offset for preview
                    this.currentSpline.build();
                    this.render();
                }
            }
            // BRUSH TOOL - Drag to draw
            else if (this.tool === 'brush') {
                // Start new brush stroke (spline) on mouse down and drag
                this.currentSpline = new SketchSpline(this);
                this.currentSpline.setOffsetSize(this.brushWidth);
                this.currentSpline.capType = this.currentCapType; // Apply current cap type
                this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                this.isDrawing = true;
                lastPoint = worldPos;
            }
            // LEG TOOL - Drag to draw tapered leg
            else if (this.tool === 'leg') {
                // Start new leg (special tapered spline)
                this.currentSpline = new SketchSpline(this);
                this.currentSpline.setType(SketchSpline.TYPE_LEG);
                this.currentSpline.setOffsetSize(this.brushWidth * SketchSpline.LEG_BRUSH_RATIO_TOP);
                this.currentSpline.centreOffset.set(1, this.brushWidth * SketchSpline.LEG_BRUSH_RATIO_BOTTOM);
                this.currentSpline.capType = SketchSpline.CAP_LEG;
                this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                this.isDrawing = true;
                lastPoint = worldPos;
            } else if (this.tool === 'select') {
                // Select tool: find and select control points
                let foundPoint = false;

                // Check all splines for nearby control points
                for (const spline of this.splines) {
                    const point = spline.findPointNear(worldPos.x, worldPos.y, 15);
                    if (point) {
                        // Clear selection on other splines unless shift is held
                        if (!e.shiftKey) {
                            this.splines.forEach(s => s.clearSelection());
                        }

                        // Select this point
                        spline.selectNode(point);
                        this.selectedSpline = spline;
                        this.draggedPoint = point;
                        this.isDrawing = true; // Use isDrawing for drag state
                        foundPoint = true;
                        this.render();
                        break;
                    }
                }

                // If no point found and not shift-clicking, clear all selections
                if (!foundPoint && !e.shiftKey) {
                    this.splines.forEach(s => s.clearSelection());
                    this.selectedSpline = null;
                    this.render();
                }
            }
        });

        // Pointer move - continue drawing or update preview
        this.canvas.addEventListener('pointermove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const worldPos = this.screenToWorld(x, y);

            // Update temporary point for offset tool (preview)
            if (this.tool === 'offset' && this.currentSpline && this.currentSpline.size() > 0) {
                // Replace the last temporary point with current mouse position
                const lastIdx = this.currentSpline.centrePath.size() - 1;
                if (lastIdx >= 0) {
                    this.currentSpline.centrePath.points[lastIdx].x = worldPos.x;
                    this.currentSpline.centrePath.points[lastIdx].y = worldPos.y;
                    this.currentSpline.build();
                    this.render();
                }
            }

            // Brush tool uses drag
            if (this.tool === 'brush' && this.isDrawing) {
                if (lastPoint && lastPoint.distanceTo(worldPos) > 5) {
                    if (this.currentSpline) {
                        this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                        this.currentSpline.build(); // Build offset paths immediately for real-time preview
                        lastPoint = worldPos;
                        this.render();
                    }
                }
            }
            // Leg tool - update endpoint position
            else if (this.tool === 'leg' && this.isDrawing) {
                if (this.currentSpline) {
                    // Leg is always straight - just two points
                    if (this.currentSpline.size() === 1) {
                        // Add the second point
                        this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                    } else {
                        // Update the last point position
                        const lastIdx = this.currentSpline.centrePath.size() - 1;
                        if (lastIdx >= 0) {
                            this.currentSpline.centrePath.points[lastIdx].x = worldPos.x;
                            this.currentSpline.centrePath.points[lastIdx].y = worldPos.y;
                        }
                    }
                    this.currentSpline.build();
                    this.render();
                }
            } else if (this.tool === 'select' && this.isDrawing && this.draggedPoint && this.selectedSpline) {
                // Drag selected point with rubber-like smoothing
                this.selectedSpline.movePointFalloff(this.draggedPoint, worldPos);

                // Rebuild offset paths in real-time
                this.selectedSpline.offset();
                this.selectedSpline.buildPath();

                // Update closed shape for 3D preview
                const splineIndex = this.splines.indexOf(this.selectedSpline);
                if (splineIndex !== -1 && splineIndex < this.closedShapes.length) {
                    this.closedShapes[splineIndex] = this.selectedSpline.getPath();
                }

                this.render();
            }
        });

        // Pointer up - finish drawing (only for brush/drag tools)
        this.canvas.addEventListener('pointerup', () => {
            if (this.isDrawing) {
                // Brush tool finishes on mouse up
                if (this.currentSpline && this.tool === 'brush') {
                    // Finish brush stroke
                    if (this.currentSpline.size() > 2) {
                        this.currentSpline.build(); // Build the offset paths
                        this.splines.push(this.currentSpline);

                        // Add spline's path to closed shapes for 3D generation
                        this.closedShapes.push(this.currentSpline.getPath());
                        console.log('Created brush stroke with', this.currentSpline.size(), 'points');

                        // Trigger shape change event
                        this.onShapeChange();
                    }
                    this.currentSpline = null;
                    this.isDrawing = false;
                }

                // Leg tool finishes on mouse up
                if (this.currentSpline && this.tool === 'leg') {
                    // Finish leg - minimum 2 points and minimum length
                    const MIN_LEG_LENGTH = 50; // pixels
                    if (this.currentSpline.size() === 2) {
                        const p0 = this.currentSpline.centrePath.points[0];
                        const p1 = this.currentSpline.centrePath.points[1];
                        const length = Math.sqrt((p1.x - p0.x) ** 2 + (p1.y - p0.y) ** 2);

                        if (length >= MIN_LEG_LENGTH) {
                            this.currentSpline.build(); // Build the offset paths with tapered caps
                            this.splines.push(this.currentSpline);

                            // Add leg to legShapes (only for outer layers)
                            this.legShapes.push(this.currentSpline.getPath());
                            console.log('Created leg with length', Math.round(length), 'pixels (outer layers only)');

                            // Trigger shape change event
                            this.onShapeChange();

                            // TODO: Generate cross-slice (Phase 5 of implementation)
                        } else {
                            console.log('Leg too short - minimum length is', MIN_LEG_LENGTH, 'pixels');
                        }
                    }
                    this.currentSpline = null;
                    this.isDrawing = false;
                }

                // Handle select tool release - optimize path (BRUSH strokes
                // only: offset paths and legs are deliberate sparse points
                // and must never be resampled)
                if (this.tool === 'select' && this.selectedSpline && this.draggedPoint) {
                    if (this.selectedSpline.type == null) {
                        this.selectedSpline.optimize();
                    }

                    // Final rebuild
                    this.selectedSpline.build();

                    // Update 3D mesh
                    const splineIndex = this.splines.indexOf(this.selectedSpline);
                    if (splineIndex !== -1 && splineIndex < this.closedShapes.length) {
                        this.closedShapes[splineIndex] = this.selectedSpline.getPath();
                        this.onShapeChange(); // Trigger 3D regeneration
                    }

                    this.draggedPoint = null;
                }

                this.isDrawing = false;
                lastPoint = null;
                this.render();
            }
        });

        // Prevent context menu
        this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

        // Keyboard controls for tool switching and brush width
        window.addEventListener('keydown', (e) => {
            // B key - Switch to brush tool
            if (e.key === 'b' || e.key === 'B') {
                this.setTool('brush');
                console.log('Switched to brush tool');
            }
            // N key - Switch to pen tool
            else if (e.key === 'n' || e.key === 'N') {
                this.setTool('pen');
                console.log('Switched to pen tool');
            }
            // S key - Switch to select tool
            else if (e.key === 's' || e.key === 'S') {
                this.setTool('select');
                console.log('Switched to select tool');
            }
            // [ key - Decrease brush width
            else if (e.key === '[' && this.tool === 'brush') {
                this.brushWidth = Math.max(5, this.brushWidth - 5);
                this.render();
                console.log('Brush width:', this.brushWidth);
            }
            // ] key - Increase brush width
            else if (e.key === ']' && this.tool === 'brush') {
                this.brushWidth = Math.min(100, this.brushWidth + 5);
                this.render();
                console.log('Brush width:', this.brushWidth);
            }
            // C key - Clear all
            else if (e.key === 'c' || e.key === 'C') {
                if (confirm('Clear all shapes?')) {
                    this.clear();
                    this.onShapeChange();
                    console.log('Cleared all shapes');
                }
            }
        });
    }

    /**
     * Phase 3.5: Handle pointer down in 3D mode (called from Application.js after ray casting)
     * @param {number} worldX - World X coordinate on plane
     * @param {number} worldY - World Y coordinate on plane
     * @param {boolean} shiftKey - Whether shift key is held
     * @param {boolean} isRightClick - Whether this is a right click
     */
    handlePointerDown3D(worldX, worldY, shiftKey = false, isRightClick = false, hitRadius = 15) {
        console.log('[3D MODE] handlePointerDown3D called, tool:', this.tool, 'isRightClick:', isRightClick, 'position:', worldX, worldY);
        const worldPos = new Vec2D(worldX, worldY);

        // PATH TOOL - Click to add points
        if (this.tool === 'pen') {
            console.log('[PATH TOOL 3D] Click detected, isRightClick:', isRightClick, 'currentPath:', this.currentPath);
            if (isRightClick && this.currentPath) {
                // Right click - finish path
                this.finishCurrentDrawing();
            } else if (!isRightClick) {
                // Clicking the FIRST point closes the polygon (>= 3 points)
                if (this.currentPath && this.currentPath.size() >= 3) {
                    const first = this.currentPath.points[0];
                    const distToFirst = Math.hypot(worldPos.x - first.x, worldPos.y - first.y);
                    if (distToFirst <= hitRadius) {
                        console.log('[PATH TOOL 3D] Clicked first point - closing polygon');
                        this.finishCurrentDrawing();
                        return;
                    }
                }

                // Left click - add point
                if (!this.currentPath) {
                    this.currentPath = new SketchPath(this);
                    console.log('[PATH TOOL 3D] Created new path');
                }
                this.currentPath.add(new SketchPoint(worldPos.x, worldPos.y));
                console.log('[PATH TOOL 3D] Added point, total points:', this.currentPath.size());
            }
        }
        // OFFSET PATH TOOL - Click to add points
        else if (this.tool === 'offset') {
            console.log('[OFFSET TOOL 3D] Click detected, isRightClick:', isRightClick, 'currentSpline:', this.currentSpline);
            if (isRightClick && this.currentSpline) {
                // Right click - finish and build offset
                this.finishCurrentDrawing();
            } else if (!isRightClick) {
                if (!this.currentSpline) {
                    // Create new offset spline
                    this.currentSpline = new SketchSpline(this);
                    this.currentSpline.setType(SketchShape.OFFSET_SPLINE);
                    this.currentSpline.setOffsetSize(this.brushWidth);
                    this.currentSpline.capType = this.currentCapType;
                    this.currentSpline.joinType = SketchSpline.JOIN_ROUND;
                    this.currentSpline.autoSmooth = false;
                    console.log('[OFFSET TOOL 3D] Created new spline');

                    // First anchor + a preview point that follows the mouse
                    this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                    this.currentSpline.add(new SketchPoint(worldPos.x + 1, worldPos.y + 1));
                } else {
                    // Commit the preview point at the clicked position...
                    const points = this.currentSpline.centrePath.points;
                    const preview = points[points.length - 1];
                    preview.x = worldPos.x;
                    preview.y = worldPos.y;

                    // ...and add a fresh preview point (nudged so the segment
                    // isn't zero-length until the mouse moves)
                    this.currentSpline.add(new SketchPoint(worldPos.x + 1, worldPos.y + 1));
                }

                console.log('[OFFSET TOOL 3D] Points:', this.currentSpline.size());

                // Build the offset for preview
                this.currentSpline.build();
            }
        }
        // BRUSH TOOL - Drag to draw
        else if (this.tool === 'brush') {
            // Start new brush stroke (spline)
            this.currentSpline = new SketchSpline(this);
            this.currentSpline.setOffsetSize(this.brushWidth);
            this.currentSpline.capType = this.currentCapType; // Apply current cap type
            this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
            this.isDrawing = true;
            this.lastPoint = worldPos;
        }
        // LEG TOOL - Drag to draw tapered leg
        else if (this.tool === 'leg') {
            // Start new leg (special tapered spline)
            this.currentSpline = new SketchSpline(this);
            this.currentSpline.setType(SketchSpline.TYPE_LEG);
            this.currentSpline.setOffsetSize(this.brushWidth * SketchSpline.LEG_BRUSH_RATIO_TOP);
            this.currentSpline.centreOffset.set(1, this.brushWidth * SketchSpline.LEG_BRUSH_RATIO_BOTTOM);
            this.currentSpline.capType = SketchSpline.CAP_LEG;
            this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
            this.isDrawing = true;
            this.lastPoint = worldPos;
        } else if (this.tool === 'select') {
            // Select tool - FIGMA-LIKE SHAPE SELECTION:
            // - overlays/handles only exist on SELECTED shapes, so handle
            //   and centre-line grabs are gated to selectedShapes
            // - clicking an anchor POINT of an unselected shape selects the
            //   shape AND grabs the point in one gesture
            // - clicking a shape's BODY selects it (shift toggles)
            // - clicking empty space clears (via the marquee plain-click)
            let foundPoint = false;

            // FIRST: Check for bezier control handles (higher priority).
            // Handles are only visible - and therefore grabbable - on
            // SELECTED shapes.
            for (const spline of this.splines) {
                if (!this.selectedShapes.has(spline)) continue;
                const handleInfo = spline.findBezierHandleNear(worldPos.x, worldPos.y, hitRadius);
                if (handleInfo) {
                    // Select this bezier handle
                    this.selectedSpline = spline;
                    this.selectedPath = null;
                    this.draggedPoint = handleInfo.handlePoint; // The Vec2D handle point
                    this.draggedHandleInfo = handleInfo; // Store full handle info for mirroring
                    this.isDrawing = true; // Use isDrawing for drag state
                    foundPoint = true;
                    console.log('[SELECT 3D] Selected bezier handle:', handleInfo.handle, 'at index:', handleInfo.index);
                    if (this.mode === '2d') this.render();
                    break;
                }
            }

            // Also check handles on pen-path (polygon) points (selected only)
            if (!foundPoint) {
                for (const path of this.paths) {
                    if (!this.selectedShapes.has(path)) continue;
                    const handleInfo = this.findHandleInList(path.points, worldPos.x, worldPos.y, hitRadius);
                    if (handleInfo) {
                        this.selectedPath = path;
                        this.selectedSpline = null;
                        this.draggedPoint = handleInfo.handlePoint;
                        this.draggedHandleInfo = handleInfo;
                        this.isDrawing = true;
                        foundPoint = true;
                        console.log('[SELECT 3D] Selected path bezier handle:', handleInfo.handle);
                        break;
                    }
                }
            }

            // SECOND: If no handle found, check for center points. A point
            // on an UNSELECTED shape selects-and-grabs in one gesture.
            if (!foundPoint) {
                for (const spline of this.splines) {
                    const point = spline.findPointNear(worldPos.x, worldPos.y, hitRadius);
                    if (point) {
                        // Ensure the shape is selected (replaces the
                        // selection unless shift adds to it)
                        this.selectShape(spline, { additive: shiftKey });

                        // Select this point
                        spline.selectNode(point);
                        this.draggedPoint = point;
                        this.draggedHandleInfo = null; // Not dragging a handle
                        this.isDrawing = true; // Use isDrawing for drag state
                        foundPoint = true;
                        console.log('[SELECT 3D] Selected center point');
                        if (this.mode === '2d') this.render();
                        break;
                    }
                }
            }

            // THIRD: Check pen-path (polygon) points (select-and-grab)
            if (!foundPoint) {
                for (const path of this.paths) {
                    const point = this.findPointInList(path.points, worldPos.x, worldPos.y, hitRadius);
                    if (point) {
                        this.selectShape(path, { additive: shiftKey });
                        this.draggedPoint = point;
                        this.draggedHandleInfo = null;
                        this.isDrawing = true;
                        foundPoint = true;
                        console.log('[SELECT 3D] Selected polygon point');
                        break;
                    }
                }
            }

            // FOURTH: No point hit - the centre LINE. On a SELECTED spline
            // this grabs the whole stroke for dragging (the thick accent
            // line affordance); on an unselected spline the first click
            // only SELECTS it (Figma-like: select first, interact second).
            if (!foundPoint) {
                const lineSpline = this.findSplineNearCentreLine(worldPos.x, worldPos.y, hitRadius * 1.2);
                if (lineSpline) {
                    if (shiftKey) {
                        // Shift: toggle the shape in the multi-selection
                        this.selectShape(lineSpline, { toggle: true });
                        console.log('[SELECT 3D] Shift-toggled shape via centre line');
                    } else if (this.selectedShapes.has(lineSpline)) {
                        this.selectedSpline = lineSpline;
                        this.selectedPath = null;
                        this.draggedPoint = null;
                        this.draggedHandleInfo = null;
                        this.draggedSpline = lineSpline;
                        this._splineDragLast = { x: worldPos.x, y: worldPos.y };
                        this.isDrawing = true;
                        console.log('[SELECT 3D] Grabbed centre line - dragging whole stroke');
                    } else {
                        this.selectShape(lineSpline);
                        console.log('[SELECT 3D] Selected shape via centre line');
                    }
                    foundPoint = true;
                    if (this.mode === '2d') this.render();
                }
            }

            // FIFTH: Shape BODY hit (outline interior) - select the shape
            // (shift toggles it in the multi-selection)
            if (!foundPoint) {
                const bodyShape = this.findShapeAt(worldPos.x, worldPos.y);
                if (bodyShape) {
                    if (shiftKey) {
                        this.selectShape(bodyShape, { toggle: true });
                    } else if (!this.selectedShapes.has(bodyShape)) {
                        this.selectShape(bodyShape);
                    }
                    foundPoint = true;
                    console.log('[SELECT 3D] Selected shape via body click');
                    if (this.mode === '2d') this.render();
                }
            }

            // SIXTH: Empty space - start a marquee (drag select). A plain
            // click (tiny rect) falls back to clearing the selection on up.
            if (!foundPoint) {
                this.marquee = {
                    x0: worldPos.x, y0: worldPos.y,
                    x1: worldPos.x, y1: worldPos.y,
                    shift: shiftKey
                };
                this.isDrawing = true;
                this.onMarqueeUpdate?.(this.marquee);
            }
        } else if (this.tool === 'bezier') {
            // BEZIER TOOL: edit curve handles on any point (splines and
            // polygons). Clicking an existing handle drags it; clicking an
            // anchor point CREATES smooth handles and starts dragging one.
            let handled = false;

            const targets = [
                ...this.splines.map(s => ({ owner: s, isSpline: true, points: s.centrePath.points })),
                ...this.paths.map(p => ({ owner: p, isSpline: false, points: p.points }))
            ];

            // Existing handle under the cursor?
            for (const target of targets) {
                const handleInfo = this.findHandleInList(target.points, worldPos.x, worldPos.y, hitRadius);
                if (handleInfo) {
                    this.selectedSpline = target.isSpline ? target.owner : null;
                    this.selectedPath = target.isSpline ? null : target.owner;
                    this.draggedPoint = handleInfo.handlePoint;
                    this.draggedHandleInfo = handleInfo;
                    this.isDrawing = true;
                    handled = true;
                    console.log('[BEZIER 3D] Dragging existing handle:', handleInfo.handle);
                    break;
                }
            }

            // Anchor point: give it smooth handles and drag the outgoing one
            if (!handled) {
                for (const target of targets) {
                    const index = target.points.findIndex(p =>
                        Math.hypot(p.x - worldPos.x, p.y - worldPos.y) <= hitRadius);
                    if (index === -1) continue;

                    const point = target.points[index];
                    if (!point.controlPoint1 && !point.controlPoint2) {
                        this.createSmoothHandles(target.points, index);
                        console.log('[BEZIER 3D] Created smooth handles on point', index);
                    }

                    this.selectedSpline = target.isSpline ? target.owner : null;
                    this.selectedPath = target.isSpline ? null : target.owner;
                    this.draggedPoint = point.controlPoint2 || point.controlPoint1;
                    this.draggedHandleInfo = {
                        point: point,
                        handle: point.controlPoint2 ? 'controlPoint2' : 'controlPoint1',
                        handlePoint: point.controlPoint2 || point.controlPoint1,
                        index: index
                    };
                    this.isDrawing = true;
                    handled = true;
                    break;
                }
            }

            if (!handled) {
                this.selectedSpline = null;
                this.selectedPath = null;
                this.draggedHandleInfo = null;
            }
        }
    }

    /**
     * Find a SketchPoint near (x, y) in a point list
     * @returns {SketchPoint|null}
     */
    findPointInList(points, x, y, radius) {
        for (const p of points) {
            if (Math.hypot(p.x - x, p.y - y) <= radius) return p;
        }
        return null;
    }

    /**
     * Find a bezier control handle near (x, y) in a point list
     * @returns {{point, handle, handlePoint, index}|null}
     */
    findHandleInList(points, x, y, radius) {
        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            for (const handle of ['controlPoint1', 'controlPoint2']) {
                const hp = p[handle];
                if (hp && Math.hypot(hp.x - x, hp.y - y) <= radius) {
                    return { point: p, handle, handlePoint: hp, index: i };
                }
            }
        }
        return null;
    }

    /**
     * Give a point symmetric bezier handles along the local tangent
     * (neighbour-to-neighbour direction), converting a corner to a curve
     */
    createSmoothHandles(points, index) {
        const n = points.length;
        const p = points[index];
        const prev = points[(index - 1 + n) % n];
        const next = points[(index + 1) % n];

        let tx = next.x - prev.x;
        let ty = next.y - prev.y;
        const tLen = Math.hypot(tx, ty);
        if (tLen < 1e-6) { tx = 1; ty = 0; } else { tx /= tLen; ty /= tLen; }

        const distPrev = Math.hypot(p.x - prev.x, p.y - prev.y);
        const distNext = Math.hypot(p.x - next.x, p.y - next.y);
        const handleLen = Math.max(10, Math.min(distPrev, distNext) / 3);

        p.controlPoint1 = new Vec2D(p.x - tx * handleLen, p.y - ty * handleLen);
        p.controlPoint2 = new Vec2D(p.x + tx * handleLen, p.y + ty * handleLen);
    }

    /**
     * Phase 3.5: Handle pointer move in 3D mode (called from Application.js after ray casting)
     * @param {number} worldX - World X coordinate on plane
     * @param {number} worldY - World Y coordinate on plane
     */
    handlePointerMove3D(worldX, worldY) {
        const worldPos = new Vec2D(worldX, worldY);

        // Update temporary point for offset tool (preview)
        if (this.tool === 'offset' && this.currentSpline && this.currentSpline.size() > 0) {
            // Replace the last temporary point with current mouse position
            const lastIdx = this.currentSpline.centrePath.size() - 1;
            if (lastIdx >= 0) {
                this.currentSpline.centrePath.points[lastIdx].x = worldPos.x;
                this.currentSpline.centrePath.points[lastIdx].y = worldPos.y;
                this.currentSpline.build();
            }
        }

        // Brush tool uses drag
        if (this.tool === 'brush' && this.isDrawing) {
            if (this.lastPoint && this.lastPoint.distanceTo(worldPos) > 5) {
                if (this.currentSpline) {
                    this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                    this.currentSpline.build(); // Build offset paths immediately for real-time preview
                    this.lastPoint = worldPos;
                }
            }
        }
        // Leg tool - update endpoint position
        else if (this.tool === 'leg' && this.isDrawing) {
            if (this.currentSpline) {
                // Leg is always straight - just two points
                if (this.currentSpline.size() === 1) {
                    // Add the second point
                    this.currentSpline.add(new SketchPoint(worldPos.x, worldPos.y));
                } else {
                    // Update the last point position
                    const lastIdx = this.currentSpline.centrePath.size() - 1;
                    if (lastIdx >= 0) {
                        this.currentSpline.centrePath.points[lastIdx].x = worldPos.x;
                        this.currentSpline.centrePath.points[lastIdx].y = worldPos.y;
                    }
                }
                this.currentSpline.build();
            }
        } else if (this.tool === 'select' && this.isDrawing && this.marquee) {
            this.marquee.x1 = worldPos.x;
            this.marquee.y1 = worldPos.y;
            this.onMarqueeUpdate?.(this.marquee);
        } else if (this.tool === 'select' && this.isDrawing && this.draggedSpline) {
            // Whole-spline drag via the centre line: translate every centre
            // point (and bezier handles) by the pointer delta
            const dx = worldPos.x - this._splineDragLast.x;
            const dy = worldPos.y - this._splineDragLast.y;
            this._splineDragLast = { x: worldPos.x, y: worldPos.y };
            for (const pt of this.draggedSpline.centrePath.points) {
                pt.x += dx; pt.y += dy;
                if (pt.controlPoint1) { pt.controlPoint1.x += dx; pt.controlPoint1.y += dy; }
                if (pt.controlPoint2) { pt.controlPoint2.x += dx; pt.controlPoint2.y += dy; }
            }
            this.draggedSpline.offset();
            this.draggedSpline.buildPath();
            this.rebuildShapeLists();
            if (this.mode === '2d') this.render();
            else if (this.mode === '3d') this.onDragPreview();
        } else if ((this.tool === 'select' || this.tool === 'bezier') && this.isDrawing &&
                   this.draggedPoint && (this.selectedSpline || this.selectedPath)) {
            // Check if we're dragging a bezier handle or a center point
            if (this.draggedHandleInfo) {
                // DRAGGING BEZIER HANDLE: Update handle position and mirror opposite handle
                const handleInfo = this.draggedHandleInfo;
                const parentPoint = handleInfo.point;

                // Update the dragged handle position
                handleInfo.handlePoint.x = worldPos.x;
                handleInfo.handlePoint.y = worldPos.y;

                // Mirror the opposite handle for smooth curves
                const handleDir = new Vec2D(
                    handleInfo.handlePoint.x - parentPoint.x,
                    handleInfo.handlePoint.y - parentPoint.y
                );
                const handleDist = handleDir.magnitude();

                // Get the opposite handle
                const oppositeHandle = handleInfo.handle === 'controlPoint1'
                    ? parentPoint.controlPoint2
                    : parentPoint.controlPoint1;

                if (oppositeHandle) {
                    // Calculate current distance of opposite handle
                    const oppositeDist = Math.sqrt(
                        (oppositeHandle.x - parentPoint.x) ** 2 +
                        (oppositeHandle.y - parentPoint.y) ** 2
                    );

                    // Mirror direction and maintain distance
                    const mirroredDir = new Vec2D(-handleDir.x, -handleDir.y);
                    const normalized = mirroredDir.normalize();

                    oppositeHandle.x = parentPoint.x + normalized.x * oppositeDist;
                    oppositeHandle.y = parentPoint.y + normalized.y * oppositeDist;
                }

                console.log('[SELECT DRAG] Dragging bezier handle, mirroring opposite');
            } else if (this.selectedSpline) {
                // DRAGGING CENTER POINT: Use rubber-band smoothing
                this.selectedSpline.movePointFalloff(this.draggedPoint, worldPos);
                console.log('[SELECT DRAG] Dragging center point with rubber-band');
            } else {
                // DRAGGING POLYGON POINT: deliberate clicked point, move 1:1
                this.draggedPoint.x = worldPos.x;
                this.draggedPoint.y = worldPos.y;
            }

            // Rebuild offset paths in real-time (splines only - polygon
            // paths ARE the shape, mutation is enough)
            if (this.selectedSpline) {
                this.selectedSpline.offset();
                this.selectedSpline.buildPath();

                // Refresh the shape lists. NOTE: closedShapes is NOT
                // index-parallel with splines (pen polygons live there too) -
                // indexing by splines.indexOf overwrote other shapes.
                this.rebuildShapeLists();
            }

            // Render (2D mode) or trigger lightweight preview (3D mode).
            // Full mesh/slice regeneration is deferred to pointer-up -
            // onShapeChange per pointermove regenerates all cross-slices and
            // slots, which makes dragging unusably slow.
            if (this.mode === '2d') {
                this.render();
            } else if (this.mode === '3d') {
                this.onDragPreview();
            }
        }
    }

    /**
     * Phase 3.5: Handle pointer up in 3D mode (called from Application.js)
     */
    handlePointerUp3D() {
        if (this.isDrawing) {
            // Brush tool finishes on mouse up
            if (this.currentSpline && this.tool === 'brush') {
                // Finish brush stroke
                if (this.currentSpline.size() > 2) {
                    this.onBeforeCommit();
                    this.currentSpline.build(); // Build the offset paths
                    this.splines.push(this.currentSpline);

                    // Add spline's path to closed shapes for 3D generation
                    this.closedShapes.push(this.currentSpline.getPath());
                    console.log('Created brush stroke with', this.currentSpline.size(), 'points');

                    // Trigger shape change event
                    this.onShapeChange();
                }
                this.currentSpline = null;
                this.isDrawing = false;
            }

            // Leg tool finishes on mouse up
            if (this.currentSpline && this.tool === 'leg') {
                // Finish leg - minimum 2 points and minimum length
                const MIN_LEG_LENGTH = 50; // pixels
                if (this.currentSpline.size() === 2) {
                    const p0 = this.currentSpline.centrePath.points[0];
                    const p1 = this.currentSpline.centrePath.points[1];
                    const length = Math.sqrt((p1.x - p0.x) ** 2 + (p1.y - p0.y) ** 2);

                    if (length >= MIN_LEG_LENGTH) {
                        this.onBeforeCommit();
                        this.currentSpline.build(); // Build the offset paths with tapered caps
                        this.splines.push(this.currentSpline);

                        // Add leg to legShapes (only for outer layers)
                        this.legShapes.push(this.currentSpline.getPath());
                        console.log('Created leg with length', Math.round(length), 'pixels (outer layers only)');

                        // Trigger shape change event
                        this.onShapeChange();

                        // TODO: Generate cross-slice (Phase 5 of implementation)
                    } else {
                        console.log('Leg too short - minimum length is', MIN_LEG_LENGTH, 'pixels');
                    }
                }
                this.currentSpline = null;
                this.isDrawing = false;
            }

            // Marquee (drag select) release
            if (this.tool === 'select' && this.marquee) {
                const m = this.marquee;
                this.marquee = null;
                this.isDrawing = false;
                this.onMarqueeUpdate?.(null);

                const minX = Math.min(m.x0, m.x1), maxX = Math.max(m.x0, m.x1);
                const minY = Math.min(m.y0, m.y1), maxY = Math.max(m.y0, m.y1);

                if ((maxX - minX) < 4 && (maxY - minY) < 4) {
                    // Plain click on empty space: clear selection and drop
                    // any layer focus (all ribs/layers show as normal)
                    if (!m.shift) {
                        this.clearShapeSelection();
                        this.draggedHandleInfo = null;
                        this.onEmptyCanvasClick?.();
                    }
                } else {
                    // Marquee: selects SHAPES whose centre points fall in
                    // the rect, keeping the point-level selection within
                    // them (the Width widget then applies to those points)
                    if (!m.shift) {
                        this.selectedShapes.clear();
                        this.splines.forEach(sp => sp.clearSelection());
                    }
                    let lastWithNodes = null;
                    for (const spline of this.splines) {
                        for (const pt of spline.centrePath?.points || []) {
                            if (pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY) {
                                spline.selectNode(pt);
                            }
                        }
                        if (spline.selectedNodes.length > 0) {
                            lastWithNodes = spline;
                            this.selectedShapes.add(spline);
                        }
                    }
                    // Pen paths: shape-level selection when any point falls
                    // inside the rect
                    let lastPath = null;
                    for (const path of this.paths) {
                        if ((path.points || []).some(pt =>
                            pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY)) {
                            this.selectedShapes.add(path);
                            lastPath = path;
                        }
                    }
                    this.selectedSpline = lastWithNodes;
                    this.selectedPath = lastWithNodes ? null : lastPath;
                    const total = this.splines.reduce((n, sp) => n + sp.selectedNodes.length, 0);
                    console.log(`Marquee selected ${total} point(s) across ${this.selectedShapes.size} shape(s)`);
                }
                if (this.mode === '2d') this.render();
            }

            // Whole-spline (centre line) drag release
            if (this.tool === 'select' && this.draggedSpline) {
                this.draggedSpline.build();
                this.rebuildShapeLists();
                this.onShapeChange();
                this.draggedSpline = null;
                this._splineDragLast = null;
                this.isDrawing = false;
            }

            // Handle select/bezier tool release
            if ((this.tool === 'select' || this.tool === 'bezier') && this.draggedPoint) {
                if (this.selectedSpline) {
                    // Optimize/resample the path for smooth curves - BRUSH
                    // strokes only (type null). Offset paths (type 4) and
                    // legs (43) are deliberate sparse clicked points:
                    // resampling them sprays extra points along the path.
                    // Also skipped for handle drags (resampling would eat
                    // the curve the handle just shaped).
                    if (!this.draggedHandleInfo && this.selectedSpline.type == null) {
                        this.selectedSpline.optimize();
                    }

                    // Final rebuild
                    this.selectedSpline.build();

                    // Refresh shape lists (closedShapes is NOT index-parallel
                    // with splines - pen polygons live there too) and
                    // trigger 3D regeneration
                    this.rebuildShapeLists();
                    this.onShapeChange();
                } else if (this.selectedPath) {
                    // Polygon path: the path object IS the shape - just
                    // trigger regeneration
                    this.onShapeChange();
                }

                this.draggedPoint = null;
                this.draggedHandleInfo = null;
            }

            this.isDrawing = false;
            this.lastPoint = null;
            if (this.mode === '2d') this.render();
        }
    }

    /**
     * Convert screen coordinates to world coordinates
     * @param {number} screenX - Screen X
     * @param {number} screenY - Screen Y
     * @returns {Vec2D} World position
     */
    screenToWorld(screenX, screenY) {
        const x = (screenX - this.viewOffset.x) / this.viewScale;
        const y = (screenY - this.viewOffset.y) / this.viewScale;

        if (this.snapToGrid) {
            return new Vec2D(
                Math.round(x / this.gridSize) * this.gridSize,
                Math.round(y / this.gridSize) * this.gridSize
            );
        }

        return new Vec2D(x, y);
    }

    /**
     * Convert world coordinates to screen coordinates
     * @param {number} worldX - World X
     * @param {number} worldY - World Y
     * @returns {Vec2D} Screen position
     */
    worldToScreen(worldX, worldY) {
        return new Vec2D(
            worldX * this.viewScale + this.viewOffset.x,
            worldY * this.viewScale + this.viewOffset.y
        );
    }

    /**
     * Add a path to the sketch
     * @param {SketchPath} path - Path to add
     */
    addPath(path) {
        this.paths.push(path);
        this.render();
    }

    /**
     * Remove a path from the sketch
     * @param {SketchPath} path - Path to remove
     */
    removePath(path) {
        const index = this.paths.indexOf(path);
        if (index !== -1) {
            this.paths.splice(index, 1);
            this.render();
        }
    }

    /**
     * Clear all paths
     */
    clear() {
        this.paths = [];
        this.splines = [];
        this.closedShapes = [];
        this.legShapes = [];
        this.currentPath = null;
        this.currentSpline = null;
        this.selectedPath = null;
        this.selectedSpline = null;
        this.selectedShapes?.clear();
        this.render();
    }

    /**
     * Render the sketch
     */
    render() {
        // Phase 3.5: Skip rendering in 3D mode (no canvas)
        if (this.mode === '3d' || !this.canvas || !this.ctx) {
            return;
        }

        // Clear canvas
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Fill background
        this.ctx.fillStyle = '#f5f5f5';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Save context state
        this.ctx.save();

        // Apply view transform
        this.ctx.translate(this.viewOffset.x, this.viewOffset.y);
        this.ctx.scale(this.viewScale, this.viewScale);

        // Draw grid if enabled
        if (this.showGrid) {
            this.renderGrid();
        }

        // Draw all paths
        this.paths.forEach(path => {
            path.render(this.ctx);
        });

        // Draw all splines (brush strokes)
        // Show control points when select tool is active
        const showControlPoints = (this.tool === 'select');
        this.splines.forEach(spline => {
            spline.render(this.ctx, showControlPoints);
        });

        // Draw current path being drawn (PATH TOOL)
        if (this.currentPath && this.tool === 'pen') {
            this.ctx.save();
            this.ctx.strokeStyle = '#FF0000'; // Red line
            this.ctx.lineWidth = 2;
            this.ctx.beginPath();
            for (let i = 0; i < this.currentPath.size(); i++) {
                const p = this.currentPath.points[i];
                if (i === 0) {
                    this.ctx.moveTo(p.x, p.y);
                } else {
                    this.ctx.lineTo(p.x, p.y);
                }
            }
            this.ctx.stroke();

            // Draw points
            this.ctx.fillStyle = '#FF0000';
            for (let i = 0; i < this.currentPath.size(); i++) {
                const p = this.currentPath.points[i];
                this.ctx.beginPath();
                this.ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
                this.ctx.fill();
            }
            this.ctx.restore();
        }

        // Draw current spline being drawn (OFFSET TOOL)
        if (this.currentSpline && this.tool === 'offset') {
            // Rebuild on the fly for real-time preview
            this.currentSpline.build();
            this.currentSpline.render(this.ctx, false);

            // Also draw the center path in red
            this.ctx.save();
            this.ctx.strokeStyle = '#FF0000'; // Red line for center path
            this.ctx.lineWidth = 2;
            this.ctx.beginPath();
            for (let i = 0; i < this.currentSpline.centrePath.size(); i++) {
                const p = this.currentSpline.centrePath.points[i];
                if (i === 0) {
                    this.ctx.moveTo(p.x, p.y);
                } else {
                    this.ctx.lineTo(p.x, p.y);
                }
            }
            this.ctx.stroke();

            // Draw center points
            this.ctx.fillStyle = '#FF0000';
            for (let i = 0; i < this.currentSpline.centrePath.size(); i++) {
                const p = this.currentSpline.centrePath.points[i];
                this.ctx.beginPath();
                this.ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
                this.ctx.fill();
            }
            this.ctx.restore();
        }

        // Draw current spline being drawn (BRUSH TOOL)
        if (this.currentSpline && this.tool === 'brush') {
            // Rebuild on the fly for real-time preview
            this.currentSpline.build();
            this.currentSpline.render(this.ctx, showControlPoints);
        }

        // Draw current leg being drawn (LEG TOOL)
        if (this.currentSpline && this.tool === 'leg') {
            // Rebuild on the fly for real-time preview
            this.currentSpline.build();
            this.currentSpline.render(this.ctx, showControlPoints);
        }

        // Restore context state
        this.ctx.restore();

        // Draw UI overlay (in screen space)
        this.renderUI();
    }

    /**
     * Render grid
     */
    renderGrid() {
        const width = this.canvas.width / this.viewScale;
        const height = this.canvas.height / this.viewScale;
        const startX = -this.viewOffset.x / this.viewScale;
        const startY = -this.viewOffset.y / this.viewScale;

        this.ctx.strokeStyle = '#e0e0e0';
        this.ctx.lineWidth = 0.5;

        // Vertical lines
        for (let x = Math.floor(startX / this.gridSize) * this.gridSize;
             x < startX + width;
             x += this.gridSize) {
            this.ctx.beginPath();
            this.ctx.moveTo(x, startY);
            this.ctx.lineTo(x, startY + height);
            this.ctx.stroke();
        }

        // Horizontal lines
        for (let y = Math.floor(startY / this.gridSize) * this.gridSize;
             y < startY + height;
             y += this.gridSize) {
            this.ctx.beginPath();
            this.ctx.moveTo(startX, y);
            this.ctx.lineTo(startX + width, y);
            this.ctx.stroke();
        }
    }

    /**
     * Render UI overlay
     */
    renderUI() {
        // Draw tool indicator
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        this.ctx.fillRect(10, 10, 150, 30);
        this.ctx.fillStyle = '#ffffff';
        this.ctx.font = '14px monospace';
        this.ctx.fillText(`Tool: ${this.tool}`, 20, 30);

        // Draw shape count
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        this.ctx.fillRect(10, 50, 150, 30);
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillText(`Shapes: ${this.paths.length + this.splines.length}`, 20, 70);

        // Draw brush width if brush tool is active
        if (this.tool === 'brush') {
            this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            this.ctx.fillRect(10, 90, 150, 30);
            this.ctx.fillStyle = '#ffffff';
            this.ctx.fillText(`Brush: ${this.brushWidth}px`, 20, 110);
        }
    }

    /**
     * Set active tool
     * @param {string} tool - Tool name ('pen', 'select', 'move')
     */
    setTool(tool) {
        // Discard any in-progress click-based drawing when switching tools
        if (tool !== this.tool) {
            this.cancelCurrentDrawing();
        }

        this.tool = tool;

        // Update cursor style based on tool (only in 2D mode with canvas)
        if (this.canvas) {
            if (tool === 'select') {
                this.canvas.style.cursor = 'pointer';
            } else {
                this.canvas.style.cursor = 'crosshair';
            }
        }

        this.render();
    }

    /**
     * Finish the in-progress click-based drawing (offset spline or pen path).
     * Triggered by right-click or Enter. Commits the shape if it has enough
     * points, otherwise discards it.
     */
    finishCurrentDrawing() {
        if (this.currentSpline && this.tool === 'offset') {
            // Remove the mouse-following preview point
            if (this.currentSpline.centrePath.size() > 1) {
                this.currentSpline.centrePath.points.pop();
            }

            // 2 CENTRE points are enough: the built outline is a full capsule
            // (quad + end caps). The old `> 2` guard counted centre-path
            // points as if they were outline vertices and silently discarded
            // valid 2-point offset paths on right-click finish.
            if (this.currentSpline.size() >= 2) {
                this.onBeforeCommit();
                this.currentSpline.setClosed(true);
                this.currentSpline.build();
                this.splines.push(this.currentSpline);
                this.closedShapes.push(this.currentSpline.getPath());
                this.onShapeChange();
                console.log('[OFFSET TOOL 3D] Spline finished:', this.currentSpline.size(), 'points');
            } else {
                console.log('[OFFSET TOOL 3D] Too few points, discarding spline');
            }
            this.currentSpline = null;
        }

        if (this.currentPath && this.tool === 'pen') {
            if (this.currentPath.size() > 2) {
                this.onBeforeCommit();
                if (this.autoClose) {
                    this.currentPath.setClosed(true);
                    this.closedShapes.push(this.currentPath);
                }
                this.paths.push(this.currentPath);
                this.onShapeChange();
                console.log('[PATH TOOL 3D] Path finished:', this.currentPath.size(), 'points');
            }
            this.currentPath = null;
        }
    }

    /**
     * Delete the currently selected spline (Delete/Backspace key).
     * Removes it from splines and rebuilds the derived shape lists.
     * @returns {boolean} True if something was deleted
     */
    deleteSelected() {
        if (!this.selectedSpline) return false;

        const index = this.splines.indexOf(this.selectedSpline);
        if (index === -1) return false;

        this.onBeforeCommit();
        this.selectedShapes.delete(this.selectedSpline);
        this.splines.splice(index, 1);
        this.selectedSpline = null;
        this.draggedPoint = null;
        this.draggedHandleInfo = null;

        this.rebuildShapeLists();
        this.onShapeChange();
        if (this.mode === '2d') this.render();

        console.log('[SKETCH] Deleted selected spline');
        return true;
    }

    /**
     * Rebuild closedShapes/legShapes from the current splines (plus any
     * pen-tool paths that were closed into shapes). Used after deletion,
     * where identity-based removal is unreliable because build() replaces
     * the outline path object.
     */
    rebuildShapeLists() {
        const penShapes = this.closedShapes.filter(p => this.paths.includes(p));

        this.closedShapes = [
            ...this.splines
                .filter(s => s.type !== SketchSpline.TYPE_LEG)
                .map(s => s.getPath()),
            ...penShapes
        ];

        this.legShapes = this.splines
            .filter(s => s.type === SketchSpline.TYPE_LEG)
            .map(s => s.getPath());
    }

    /**
     * Discard any in-progress drawing without committing it
     * (Escape key or tool switch)
     */
    cancelCurrentDrawing() {
        if (this.currentSpline || this.currentPath) {
            console.log('[SKETCH] Cancelled in-progress drawing');
        }
        this.currentSpline = null;
        this.currentPath = null;
        this.isDrawing = false;
        this.lastPoint = null;
    }

    /**
     * Set view offset
     * @param {number} x - X offset
     * @param {number} y - Y offset
     */
    setViewOffset(x, y) {
        this.viewOffset.set(x, y);
        this.render();
    }

    /**
     * Set view scale
     * @param {number} scale - Scale factor
     */
    setViewScale(scale) {
        this.viewScale = Math.max(0.1, Math.min(10, scale));
        this.render();
    }

    /**
     * Get sketch globals (for compatibility)
     * @returns {Object} Globals
     */
    getSketchGlobals() {
        return this.sketchGlobals;
    }

    /**
     * Resize canvas
     * @param {number} width - New width
     * @param {number} height - New height
     */
    resize(width, height) {
        this.canvas.width = width;
        this.canvas.height = height;
        this.render();
    }

    /**
     * Set parent SlicePlane (for integration with 3D geometry)
     * @param {SlicePlane} slicePlane - Parent slice plane
     */
    setOnSlicePlane(slicePlane) {
        this.slicePlane = slicePlane;
    }

    /**
     * Get height of all paths (max Y - min Y)
     * @returns {number} Height in pixels
     */
    getHeight() {
        if (this.closedShapes.length === 0) return 0;

        let minY = Infinity;
        let maxY = -Infinity;

        this.closedShapes.forEach(path => {
            const bounds = path.getBounds();
            minY = Math.min(minY, bounds.minY);
            maxY = Math.max(maxY, bounds.maxY);
        });

        return maxY - minY;
    }

    /**
     * Get minimum Y coordinate of all paths
     * @returns {number} Min Y in pixels
     */
    getMinY() {
        if (this.closedShapes.length === 0) return 0;

        let minY = Infinity;
        this.closedShapes.forEach(path => {
            const bounds = path.getBounds();
            minY = Math.min(minY, bounds.minY);
        });

        return minY;
    }

    /**
     * Get maximum Y coordinate of all paths
     * @returns {number} Max Y in pixels
     */
    getMaxY() {
        if (this.closedShapes.length === 0) return 0;

        let maxY = -Infinity;
        this.closedShapes.forEach(path => {
            const bounds = path.getBounds();
            maxY = Math.max(maxY, bounds.maxY);
        });

        return maxY;
    }

    /**
     * Get total area of all closed shapes
     * @returns {number} Area in square pixels
     */
    getArea() {
        let totalArea = 0;
        this.closedShapes.forEach(path => {
            totalArea += path.getArea();
        });
        return totalArea;
    }

    /**
     * Get center of mass of all closed shapes
     * @returns {Vec2D|null} Center of mass
     */
    getCentreOfMass() {
        if (this.closedShapes.length === 0) return null;

        let totalArea = 0;
        let weightedX = 0;
        let weightedY = 0;

        this.closedShapes.forEach(path => {
            const area = path.getArea();
            const bounds = path.getBounds();
            const centerX = (bounds.minX + bounds.maxX) / 2;
            const centerY = (bounds.minY + bounds.maxY) / 2;

            weightedX += centerX * area;
            weightedY += centerY * area;
            totalArea += area;
        });

        if (totalArea === 0) return null;

        return new Vec2D(weightedX / totalArea, weightedY / totalArea);
    }

    /**
     * Clone this sketch plane
     * @returns {SketchPlane} Cloned sketch plane
     */
    clone() {
        const cloned = new SketchPlane(document.createElement('canvas'));
        cloned.paths = this.paths.map(p => p.clone());
        cloned.closedShapes = this.closedShapes.map(p => p.clone());
        cloned.legShapes = this.legShapes.map(p => p.clone());
        cloned.viewOffset = this.viewOffset.copy();
        cloned.viewScale = this.viewScale;
        cloned.tool = this.tool;
        cloned.autoClose = this.autoClose;
        return cloned;
    }

    /**
     * Select all shapes
     */
    select() {
        this.paths.forEach(path => path.selected = true);
    }

    /**
     * Unselect all shapes
     */
    unselect() {
        this.paths.forEach(path => path.selected = false);
    }

    /**
     * Update sketch state
     */
    update() {
        // Update any dynamic properties
        this.render();
    }

    /**
     * Scale all paths
     * @param {number} scale - Scale factor
     * @param {Vec2D} centre - Center point for scaling
     */
    scale(scale, centre) {
        this.paths.forEach(path => {
            path.points.forEach(point => {
                point.sub(centre);
                point.multiplyScalar(scale);
                point.add(centre);
            });
        });
        this.render();
    }

    /**
     * Flip all paths horizontally
     * @param {Vec2D} centre - Center point for flipping
     */
    flipHorizontal(centre) {
        this.paths.forEach(path => {
            path.points.forEach(point => {
                point.x = centre.x - (point.x - centre.x);
            });
        });
        this.render();
    }

    /**
     * Export to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        return {
            paths: this.paths.map(p => p.toJSON()),
            viewOffset: this.viewOffset.toJSON(),
            viewScale: this.viewScale
        };
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     */
    fromJSON(data) {
        this.clear();

        if (data.paths) {
            data.paths.forEach(pathData => {
                const path = SketchPath.fromJSON(this, pathData);
                this.paths.push(path);
            });
        }

        if (data.viewOffset) {
            this.viewOffset = Vec2D.fromJSON(data.viewOffset);
        }

        if (data.viewScale !== undefined) {
            this.viewScale = data.viewScale;
        }

        this.render();
    }

    /**
     * Get current sketch state (for saving to base planes)
     * @returns {Object} Sketch state
     */
    getSketchState() {
        // Include currentPath if actively drawing (for real-time 3D rendering)
        const paths = this.paths.map(p => p.clone());
        if (this.currentPath && this.currentPath.size() > 0) {
            paths.push(this.currentPath.clone());
        }

        return {
            paths: paths,
            splines: this.splines.map(s => s.clone()),
            closedShapes: this.closedShapes.map(s => s.clone()),
            legShapes: this.legShapes.map(s => s.clone())
        };
    }

    /**
     * Set sketch state (for loading from base planes)
     * @param {Object} state - Sketch state
     */
    setSketchState(state) {
        if (!state) {
            this.clear();
            return;
        }

        this.paths = state.paths ? state.paths.map(p => p.clone()) : [];
        this.splines = state.splines ? state.splines.map(s => s.clone()) : [];
        this.closedShapes = state.closedShapes ? state.closedShapes.map(s => s.clone()) : [];
        this.legShapes = state.legShapes ? state.legShapes.map(s => s.clone()) : [];
        this.selectedShapes?.clear(); // shape objects were replaced by clones

        this.render();
    }

    /**
     * Create an empty sketch state
     * @returns {Object} Empty sketch state
     */
    static createEmptyState() {
        return {
            paths: [],
            splines: [],
            closedShapes: []
        };
    }
}
